import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  DndContext,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  snapCenterToCursor,
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import { sectionsActions } from 'apps/formsCreationKit/store/reducers/entities/sections'
import {
  entriesSelector,
  sectionPreviewSelector,
  sheetsSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import {
  activeEntryGuidSelector,
  activeSheetIdSelector,
} from 'apps/formsCreationKit/store/selectors/settings'
import {
  updateSheet,
  updateEntry,
  updateIndex,
} from 'apps/formsCreationKit/store/actions/sharedActions'
import {
  OutlineGroup,
  OutlineItem,
} from 'apps/studio/components/shared/Outline'
import { selectElement } from 'apps/studio/store/actions/sharedActions'
import { pagesSelector } from 'apps/studio/store/selectors/pdf'
import { appSettingsActions } from 'apps/studio/store/reducers/settings/appSettings'
import { ensureUniqueDescription } from 'apps/builder/lib/utils'

import { lib } from '@gocanvas/client'
const { selectSheet } = appSettingsActions
const { LOOP_SHEET_TYPE_ID, DEFAULT_SHEET_TYPE_ID } = lib

const SortableOutlineItem = ({ entry, activeEntryGuid, pageNumber }) => {
  const dispatch = useDispatch()
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: entry.guid,
      data: { entry },
    })
  const { guid, type, label, sheet_id } = entry
  const isActiveEntry = activeEntryGuid === guid

  return (
    <div
      ref={setNodeRef}
      style={{
        transition,
        transform: CSS.Transform.toString(transform),
      }}
      {...attributes}
      {...listeners}
    >
      <OutlineItem
        editable={true}
        type={type}
        label={label}
        updateLabel={newLabel => {
          dispatch(
            updateEntry({
              guid,
              label: newLabel,
            })
          )
        }}
        isActive={isActiveEntry}
        setIsActive={() =>
          dispatch(
            selectElement({
              entry_guid: guid,
              sheet_id,
            })
          )
        }
        pageNumber={pageNumber}
      />
    </div>
  )
}

const TableOutlineGroup = ({ keyFieldSheet, pageNumber }) => {
  const dispatch = useDispatch()
  const entries = useSelector(entriesSelector)
  const allSheets = useSelector(sheetsSelector)
  const activeEntryGuid = useSelector(activeEntryGuidSelector)
  const activeSheetId = useSelector(activeSheetIdSelector)

  // First table column - Key Field
  // others (detail) sheet.
  const detailSheet = allSheets.find(
    s => s.section_id === keyFieldSheet.multi_section
  )
  const columns = [keyFieldSheet, detailSheet].flatMap(sheet =>
    entries
      .filter(e => e.sheet_id === sheet.id)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  )

  return (
    <OutlineGroup
      title={keyFieldSheet.description || 'Table'}
      type="table"
      editable
      updateTitle={newDescription => {
        dispatch(
          updateSheet({
            id: keyFieldSheet.id,
            section_id: keyFieldSheet.section_id,
            description: ensureUniqueDescription(
              allSheets,
              newDescription,
              keyFieldSheet.id
            ),
          })
        )
      }}
      isActive={activeSheetId === keyFieldSheet.id && !activeEntryGuid}
      setIsActive={() => dispatch(selectSheet({ sheet_id: keyFieldSheet.id }))}
      pageNumber={pageNumber}
    >
      {columns.map(col => (
        <OutlineItem
          key={`outline-col-${col.guid}`}
          type={col.type}
          label={col.label}
          isActive={activeEntryGuid === col.guid}
          setIsActive={() =>
            dispatch(
              selectElement({ entry_guid: col.guid, sheet_id: col.sheet_id })
            )
          }
          pageNumber={pageNumber}
          editable={true}
          updateLabel={newLabel => {
            dispatch(
              updateEntry({
                guid: col.guid,
                label: newLabel,
              })
            )
          }}
        />
      ))}
    </OutlineGroup>
  )
}

const EntriesSortableList = ({
  entries,
  activeEntryGuid,
  pageNumber,
  sensors,
  onDragStart,
  onDragEnd,
}) => (
  <DndContext
    sensors={sensors}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    collisionDetection={closestCorners}
    modifiers={[restrictToVerticalAxis]}
  >
    <SortableContext
      items={entries.map(e => e.guid)}
      strategy={verticalListSortingStrategy}
    >
      {entries.map(entry => (
        <SortableOutlineItem
          key={`outline-entry-${entry.guid}`}
          entry={entry}
          activeEntryGuid={activeEntryGuid}
          pageNumber={pageNumber}
        />
      ))}
    </SortableContext>
  </DndContext>
)

const SortableOutlineGroup = ({ sheet, children, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: sheet.id,
      data: { sheet },
    })
  return (
    <div
      ref={setNodeRef}
      style={{
        transition,
        transform: CSS.Transform.toString(transform),
      }}
      {...attributes}
      {...listeners}
    >
      <OutlineGroup {...props}>{children}</OutlineGroup>
    </div>
  )
}

export const FormOutline = () => {
  const dispatch = useDispatch()
  const allSheets = useSelector(sheetsSelector)
  const entries = useSelector(entriesSelector)
  const pages = useSelector(pagesSelector)
  const sectionSheetPreview = useSelector(sectionPreviewSelector)
  const activeEntryGuid = useSelector(activeEntryGuidSelector)
  const activeSheetId = useSelector(activeSheetIdSelector)
  const [isDraggingSheet, setIsDraggingSheet] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Sets the drag activation to start once the user moves the draggable 5px.
      // This prevents accidental drags when clicking on an element.
      activationConstraint: {
        distance: 5,
      },
    })
  )

  const handleDragStart = ({ active }) => {
    if (active?.data?.current.sheet) {
      setIsDraggingSheet(true)
      dispatch(selectSheet({ sheet_id: active.data.current.sheet.id }))
    } else {
      const { entry: draggedEntry = {} } = active?.data?.current || {}
      const { guid, sheet_id } = draggedEntry
      dispatch(
        selectElement({
          entry_guid: guid,
          sheet_id,
        })
      )
    }
  }

  const handleDragEnd = ({ active, over }) => {
    setIsDraggingSheet(false)
    // Sheet/Section dragging logic
    const activeSheet = active?.data?.current?.sheet
    const overSheet = over?.data?.current?.sheet
    if (activeSheet && overSheet && activeSheet.id !== overSheet.id) {
      dispatch(
        sectionsActions.updateIndex({
          id: activeSheet.section_id,
          oldPosition: activeSheet.position,
          newPosition: overSheet.position,
        })
      )
    } else {
      // Entry dragging logic
      const { entry: draggedEntry = {} } = active?.data?.current || {}
      const { entry: overEntry = {} } = over?.data?.current || {}
      // For now, we can only reorder entries within the same sheet
      if (
        draggedEntry.guid !== overEntry.guid &&
        draggedEntry.sheet_id === overEntry.sheet_id
      ) {
        // Use visual array indices rather than raw stored position values so that
        // forms with duplicate or non-sequential positions (e.g. many entries all
        // at position -3) are reordered correctly.
        const sheetEntries = entries
          .filter(e => e.sheet_id === draggedEntry.sheet_id)
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        const oldPosition = sheetEntries.findIndex(
          e => e.guid === draggedEntry.guid
        )
        const newPosition = sheetEntries.findIndex(
          e => e.guid === overEntry.guid
        )
        // Guard: if either entry is not in the current Redux state (e.g. stale
        // drag data after a concurrent update), skip the dispatch to avoid
        // passing -1 to determinePosition which would corrupt all positions.
        if (oldPosition === -1 || newPosition === -1) return
        dispatch(
          updateIndex({
            guid: active.id,
            sheet: draggedEntry.sheet_id,
            oldSheet: overEntry.sheet_id,
            oldPosition,
            newPosition,
          })
        )
      }
    }
  }
  // TODO: We might benefit from standardizing the panel markup into it's
  // own component now that we can see where the repeated markup occurs
  return (
    <div className="gcds--control-panel workspace-panel">
      <div className="panel--info is-sticky">
        <div className="panel--title">Form Outline</div>
      </div>
      <div className="panel--content --workspace-panel--content">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={[
            snapCenterToCursor,
            restrictToFirstScrollableAncestor,
            restrictToVerticalAxis,
          ]}
        >
          <SortableContext
            items={sectionSheetPreview.reduce((ids, sheet) => {
              if (sheet.sheet_type_id !== DEFAULT_SHEET_TYPE_ID) {
                ids.push(sheet.id)
              }
              return ids
            }, [])}
            strategy={verticalListSortingStrategy}
          >
            {[...pages]
              .sort((a, b) => a.page_number - b.page_number)
              .map(page => {
                const pageSheets = sectionSheetPreview.filter(sheet =>
                  page.section_ids.includes(sheet.section_id)
                )
                if (!pageSheets.length) return null
                return (
                  <OutlineGroup
                    key={`page-${page.id}-outline`}
                    title={`Page ${page.page_number}`}
                    type="page"
                    editable={false}
                    pageNumber={page.page_number}
                  >
                    {pageSheets.map(sheet => {
                      const { id: sheetId, section_id, description } = sheet

                      // Key Field
                      if (
                        sheet.sheet_type_id === LOOP_SHEET_TYPE_ID &&
                        sheet.multi_section
                      ) {
                        return (
                          <TableOutlineGroup
                            key={`table-${sheetId}-outline`}
                            keyFieldSheet={sheet}
                            pageNumber={page.page_number}
                          />
                        )
                      }

                      const sheetEntries = entries
                        .filter(e => e.sheet_id === sheetId)
                        .sort((a, b) => a.position - b.position)

                      // Default PDF screen sheet: render entries flat as
                      // siblings of TableOutlineGroup under the page group,
                      // skipping the redundant "Page N" sheet wrapper.
                      if (sheet.sheet_type_id === DEFAULT_SHEET_TYPE_ID) {
                        if (sheetEntries.length === 0) return null
                        return (
                          <EntriesSortableList
                            key={`sheet-${sheetId}-flat`}
                            entries={sheetEntries}
                            activeEntryGuid={activeEntryGuid}
                            pageNumber={page.page_number}
                            sensors={sensors}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                          />
                        )
                      }

                      const isActiveSheet =
                        activeSheetId === sheetId && !activeEntryGuid

                      return (
                        <SortableOutlineGroup
                          key={`sheet-${sheetId}-outline`}
                          sheet={sheet}
                          editable={true}
                          title={description}
                          updateTitle={newDescription => {
                            dispatch(
                              updateSheet({
                                id: sheetId,
                                section_id,
                                description: ensureUniqueDescription(
                                  allSheets,
                                  newDescription,
                                  sheetId
                                ),
                              })
                            )
                          }}
                          isActive={isActiveSheet}
                          setIsActive={() =>
                            dispatch(selectSheet({ sheet_id: sheetId }))
                          }
                          type="screen"
                          shouldDisplayGroup={isDraggingSheet}
                          pageNumber={page.page_number}
                        >
                          {sheetEntries.length > 0 && (
                            <EntriesSortableList
                              entries={sheetEntries}
                              activeEntryGuid={activeEntryGuid}
                              pageNumber={page.page_number}
                              sensors={sensors}
                              onDragStart={handleDragStart}
                              onDragEnd={handleDragEnd}
                            />
                          )}
                        </SortableOutlineGroup>
                      )
                    })}
                  </OutlineGroup>
                )
              })}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  )
}
